/**
 * Tracker API — the ported CFM Tracker (list/detail/equity + dual-write ops).
 *
 * All metrics are computed server-side from the mirrored trade-history
 * tables; marks resolve from whichever host holds the IB session. A null
 * metric means the input (usually a live mark) is missing — render "—".
 */
import { apiGet, apiPost, apiPut, apiDelete, client } from './client'

export interface TrackerPosition {
  id: number
  broker_contract_id: string | null
  osi_symbol: string | null
  underlying: string | null
  /** Strict /GC form ('/ES' for futures) — preferred for display. */
  underlying_display?: string | null
  /** Earnings land on/before this leg's expiry (event risk). */
  earnings_before_expiry?: boolean
  next_earnings_date?: string | null
  status: 'OPEN' | 'PARTIAL' | 'CLOSED'
  is_base: boolean
  cached_net_qty: number
  cached_open_price: number | null
  cached_close_price: number | null
  cached_commissions: number
  original_quantity: number
  multiplier: number
  /** The leg this one rolled from; null = chain root or a fresh campaign. */
  rolled_from_position_id?: number | null
  symbol: string | null
  strike: number | null
  expiry: string | null
  put_call: 'C' | 'P' | null
  position_type: string | null
  net_pnl: number | null
  opened_at: string | null
  closed_at: string | null
  underlying_at_open: number | null
  underlying_at_close: number | null
  base_at_open: number | null
  base_at_close: number | null
  juice_at_open: number | null
  juice_at_close: number | null
  cushion_at_open: number | null
  cushion_at_close: number | null
  total_juice: number | null
  total_cushion: number | null
}

export interface TrackerTradeSummary {
  id: number
  trade_name: string
  underlying: string
  /** Strict /GC form ('/ES' for futures) — preferred for display. */
  underlying_display?: string
  /** Next earnings date (ISO) across allowed underlyings; null/absent = unknown. */
  next_earnings_date?: string | null
  /** Any OPEN short leg has earnings on/before its expiry. */
  earnings_before_short_expiry?: boolean
  /** Explicitly allowed additional tickers (multi-asset campaign, e.g. the
   *  SPX/ES/SPY sizing ladder). [] = normal single-ticker trade. */
  extra_underlyings?: string[]
  extra_underlyings_display?: string[]
  trade_type: 'CFM' | 'JL' | 'JP' | string
  /** Linked Compass plan (decision graph); null = no plan. */
  plan_id?: string | null
  plan_name?: string | null
  status: 'ACTIVE' | 'CLOSED'
  /** Derived: any linked Position.status=OPEN (shorts or base). Not stored status. */
  has_open_position?: boolean
  /** Product lifecycle: open = stored ACTIVE, closed = stored CLOSED. */
  lifecycle?: 'open' | 'closed'
  /** Product presence under Open: active | inactive. Null when Closed. */
  book_presence?: 'active' | 'inactive' | null
  notes: string | null
  opened_at: string | null
  closed_at: string | null
  total_base_cost: number
  total_base_shares: number
  total_short_income: number
  num_open_shorts: number
  num_closed_shorts: number
  cumulative_net_premium: number
  cumulative_juice_income: number
  /** Realized intrinsic (cushion) income on closed short legs. */
  cumulative_cushion_income: number
  original_cost_basis_per_share: number | null
  adjusted_cost_basis: number | null
  adjusted_cost_basis_per_share: number | null
  adjusted_cost_basis_closed_per_share?: number | null
  basis_anchor?: number | null
  basis_net_premium_per_share?: number | null
  basis_cushion_per_share?: number | null
  // Income floor: adjusted basis + drawn income (income_rate% × anchor × weeks).
  juice_rate?: number | null
  income_rate?: number | null
  weeks_held?: number
  income_floor_per_share?: number | null
  // True when the income floor has risen above the sold strike — the trade's
  // capped exit can't cover the drawn income (you're drawing from capital).
  income_floor_above_strike?: boolean
  // Original (cost) basis: share cost when owned, else the FIRST short-put strike.
  original_basis_per_share?: number | null
  /**
   * Share-equivalent size for capital math: total_base_shares when long, else
   * earliest short-put qty × multiplier (CSP assignment liability size).
   */
  capital_share_equiv?: number | null
  /**
   * Capital denominator for % display = original_basis_per_share × capital_share_equiv.
   * Stable under put roll-downs. Null when original basis or size is missing.
   */
  capital_denominator?: number | null
  // P&L if all open short legs expire worthless (CSP: the total trade P&L then).
  pnl_if_short_expire?: number | null
  /**
   * COMBINED despite the name: closed @ trade + open @ mark, with missing
   * net_pnl coerced to 0. Prefer `computed_realized_only` +
   * `computed_unrealized_only` (see `lib/tracker/totalPnl`) for Total P&L UI.
   */
  computed_realized_pnl: number
  /** Booked money only — closed contracts, net of commissions. Does not move with the market. */
  computed_realized_only?: number | null
  /** Open legs marked to market. This is the half that ticks. */
  computed_unrealized_only?: number | null
  /** A leg priced into net_pnl but not attributable to either side. */
  pnl_split_partial?: boolean
  max_drawdown: number
  current_underlying_price?: number | null
  // ── wheel phase (server-derived; see tracker/details.derive_collateral_state)
  phase?: WheelPhase | null
  /** Why the trade is in this phase — surfaced as the phase chip's tooltip. */
  phase_reason?: string | null
  /** What the base lot actually is, once there is one. */
  base_kind?: 'shares' | 'futures' | null
  basis_anchor_source?: 'base_cost' | 'nearest_put_strike' | null
  /** Set when the adjusted basis is null: say WHY, never a bare dash. */
  basis_null_reason?: string | null
  capital_null_reason?: string | null
  num_open_short_puts?: number
  num_open_short_calls?: number
  // ── pace against the trade's own declared juice rate
  juice_target_per_week?: number | null
  juice_actual_per_week?: number | null
  /** actual ÷ target. >=1 is on pace. Null until the trade is half a week old. */
  juice_pace_ratio?: number | null
  /**
   * Floored extrinsic still on open short options (mark − intrinsic) × qty × mult.
   * Same definition as account `juice_profit_remaining`. Null = no open shorts or incomplete.
   */
  juice_profit_remaining?: number | null
  /** True when an open short option is missing mark/spot/strike — do not treat null as $0. */
  juice_profit_remaining_incomplete?: boolean
  /** Staleness of the marks this row's live numbers came from. */
  marks_freshness?: MarksFreshness | null
  /** Roll chains within this trade (detail payload only). */
  roll_chains?: RollChain[]
}

export type WheelPhase = 'CASH' | 'SHORT_PUT' | 'BASE' | 'COVERED'

/** Successive legs of one campaign — each rolled from the one before. */
export interface RollChain {
  root_id: number
  position_ids: number[]
  length: number
  underlying: string | null
  right: string | null
  /** Premium banked across the whole chain (open legs keep their credit). */
  cum_net_premium: number
  /** What the buy-backs cost ABOVE their credit — the cost of defending. */
  defense_cost: number
  weeks_spanned: number
  open: boolean
  /** The chain ended in an assignment / exercise / expiry. */
  settled: boolean
}

export interface MarksFreshness {
  underlying_age_seconds: number | null
  underlying_source: string | null
  worst_leg_age_seconds: number | null
  legs_missing_marks: number
  legs_total: number
}

export interface MarkMeta {
  price: number
  age_seconds: number | null
  source: 'ib_direct' | 'tba_cache'
}

export interface TrackerTradeDetail extends TrackerTradeSummary {
  base_positions: TrackerPosition[]
  short_positions: TrackerPosition[]
  marks_meta?: Record<string, MarkMeta>
  underlying_mark_meta?: MarkMeta | null
  /** The trade's account, resolved server-side from its positions (for cockpit execution). */
  broker_account_id?: number | null
  account_alias?: string | null
}

export interface EquitySeriesPoint {
  date: string
  pnl: number
  pnl_percent: number
  label: string | null
  event_type: string
  long_qty: number | null
  short_qty: number | null
}

export interface EquitySeries {
  label: string
  color: string
  data: EquitySeriesPoint[]
}

export interface TrackerEquityData {
  trade_id: number
  trade_name: string
  initial_cost: number
  series: {
    short: EquitySeries
    underlying: EquitySeries
    base: EquitySeries
    combined: EquitySeries
    // "If juice realized" projection: 2-pt dashed line from the Combined
    // endpoint to the latest open-short expiry (pnl_if_short_expire). May be empty.
    projected?: EquitySeries
  }
  total_juice: number
  total_cushion: number
  base_pnl: number
  total_commissions: number
  total_pnl: number
  marks_meta?: Record<string, MarkMeta>
}

export interface TrackerListResponse {
  count: number
  trades: TrackerTradeSummary[]
}

export interface UnassignedPositionsResponse {
  count: number
  positions: TrackerPosition[]
}

export const listTrackerTrades = (params?: {
  status?: string
  /** Derived cut: open|active = Active; flat|inactive = Inactive. Not stored. */
  presence?: string
  underlying?: string
  trade_type?: string
  account?: string
}) => apiGet<TrackerListResponse>('/tracker/trades', { params })

export const getTrackerTrade = (tradeId: number) =>
  apiGet<TrackerTradeDetail>(`/tracker/trades/${tradeId}`)

export const getTrackerEquity = (tradeId: number) =>
  apiGet<TrackerEquityData>(`/tracker/trades/${tradeId}/equity-data`)

/** Live total P&L for the equity-graph "Live P&L" line. null = no live mark. */
export interface TrackerLivePnl {
  trade_id: number
  total_pnl: number | null
  total_pnl_percent: number | null
  marks_meta?: Record<string, MarkMeta>
}

export const getTrackerLivePnl = (tradeId: number) =>
  apiGet<TrackerLivePnl>(`/tracker/trades/${tradeId}/live-pnl`)

// NB: the endpoint also accepts position_types/opened_after, deliberately not
// exposed here — nothing passes them and queryKeys.tracker.unassigned doesn't
// include them, so two different filters would share one cache entry.
export const getTrackerUnassigned = (params: {
  /** Ticker, or CSV of tickers for a multi-asset trade ("SPX,ES,SPY"). */
  underlying: string
  include_closed?: boolean
  /** Compass account alias (personal/paper); omit = all accounts. */
  account?: string
}) => apiGet<UnassignedPositionsResponse>('/tracker/positions/unassigned', { params })

export const createTrackerTrade = (body: {
  trade_name: string
  underlying: string
  /** Optional Compass plan id; omit / null / "none" = no plan. */
  plan_id?: string | null
  /** Legacy type; server defaults to CFM when omitted. */
  trade_type?: string
  notes?: string
  /**
   * Positions the trade is being started from. The server derives the trade's
   * account from these, so the operator is never asked which account — a trade
   * lives where its positions live.
   */
  position_ids?: number[]
}) => apiPost<{ id: number; trade_name: string; status: string; plan_id?: string | null }>(
  '/tracker/trades',
  body,
)

export const updateTrackerTrade = (
  tradeId: number,
  body: Partial<{
    trade_name: string
    notes: string
    trade_type: string
    plan_id: string | null
    juice_rate: number | null
    income_rate: number | null
    /** Additional allowed tickers; [] clears back to single-ticker. */
    extra_underlyings: string[]
  }>,
) =>
  apiPut<{ id: number; ok: boolean }>(`/tracker/trades/${tradeId}`, body)

export const closeTrackerTrade = (tradeId: number) =>
  apiPost<{ id: number; status: string }>(`/tracker/trades/${tradeId}/close`)

export const reopenTrackerTrade = (tradeId: number) =>
  apiPost<{ id: number; status: string }>(`/tracker/trades/${tradeId}/reopen`)

export const deleteTrackerTrade = (tradeId: number) =>
  apiDelete<{ ok: boolean; deleted: number }>(`/tracker/trades/${tradeId}`)

export const assignTrackerPositions = (
  tradeId: number,
  body: { position_ids: number[]; is_base: boolean },
) => apiPost<{ success: boolean; assigned: number; errors: string[] }>(
  `/tracker/trades/${tradeId}/assign-positions`,
  body,
)

export const unassignTrackerPosition = (tradeId: number, positionId: number) =>
  apiPost<{ success: boolean; message: string }>(`/tracker/trades/${tradeId}/unassign-position`, {
    position_id: positionId,
  })

/**
 * Plain-text TradingView overlay blob for all Open trades (stored ACTIVE).
 * Paste into the "CFM Trade Overlay" Pine indicator. text/plain, not JSON.
 */
export const getTrackerOverlay = (params?: { account?: string }) =>
  client
    .get<string>('/tracker/overlay.txt', { params, responseType: 'text' })
    .then((r) => r.data)

/**
 * Auto-assign suggestions — unassigned positions matched to an Open trade
 * for their underlying (the one-click review banner). Derived live server-side;
 * confidence "ambiguous" means several Open trades share the underlying.
 */
export interface AssignmentSuggestion {
  position_id: number
  position_label: string | null
  underlying: string
  is_base: boolean
  trade_id: number
  trade_name: string | null
  confidence: 'clear' | 'ambiguous'
}

export const getAssignmentSuggestions = (params?: { account?: string }) =>
  apiGet<{ count: number; suggestions: AssignmentSuggestion[] }>(
    '/tracker/assignment-suggestions',
    { params },
  )

export const applyAssignmentSuggestions = (
  accepted: { position_id: number; trade_id: number; is_base: boolean }[],
) =>
  apiPost<{ success: boolean; assigned: number; errors: string[] }>(
    '/tracker/assignment-suggestions/apply',
    { accepted },
  )
